import { For, Show, type JSX } from 'solid-js'
import type { ViewProps } from 'wdc-cube-solid'
import {
    type LINK_COLUMNS,
    type USER_COLUMNS,
    type CompanyLinkScope,
    type CompanyUsersColumnScope,
    type CompanyUserScope,
    type CompanyUsersScope
} from 'mormaii-web-presentation/company-users'

import { CompanyPicker } from '../../../components/company-picker'
import { Button } from '../../../components/filters'
import { Tabs } from '../../../components/tabs'
import { DataTable } from '../../../components/table'
import { ErrorNotice, Panel } from '../../../components/ui'

import Css from './company-users.module.scss'

/**
 * Quem faz parte da empresa.
 *
 * Duas abas no mesmo lugar, e nao duas telas: **Solicitacoes** decide quem entra e
 * **Usuarios** administra quem ja esta. Separa-las obrigaria quem acabou de aprovar
 * um vinculo a procurar a outra tela so para conferir o resultado.
 *
 * As colunas nao ordenam. Nao e esquecimento: as duas listas sao curtas - os
 * administradores de uma empresa, e os pedidos que ainda esperam - e ordenacao aqui
 * seria um parametro de URL a mais para servir a uma lista de cinco linhas.
 */
export function CompanyUsersView(props: ViewProps<CompanyUsersScope>): JSX.Element {
    return (
        <Show when={!props.scope.error} fallback={<ErrorNotice message={props.scope.error!} />}>
            <div class={Css.screen} aria-busy={props.scope.loading}>
                <Show when={props.scope.companySelector}>
                    {(selector) => (
                        <Panel title="Empresa">
                            <CompanyPicker scope={selector()} variant="field" class={Css.picker} />
                        </Panel>
                    )}
                </Show>

                <Show
                    when={!props.scope.needsCompany}
                    fallback={
                        <Panel>
                            <p class={Css.noCompany}>Escolha uma empresa para ver quem faz parte dela.</p>
                        </Panel>
                    }
                >
                    <Tabbar scope={props.scope} />

                    <Show when={props.scope.tab === 'usuarios'} fallback={<Requests scope={props.scope} />}>
                        <Users scope={props.scope} />
                    </Show>
                </Show>
            </div>
        </Show>
    )
}

/**
 * As abas desta area, traduzidas para a forma que o componente comum entende.
 *
 * O contador so existe onde ele muda uma decisao: saber que ha tres pessoas
 * esperando e o que faz alguem ir ate a aba de solicitacoes.
 */
function Tabbar(props: { scope: CompanyUsersScope }): JSX.Element {
    return (
        <Tabs
            items={props.scope.tabs.map((tab) => ({
                key: tab.key,
                label: tab.label,
                badge: tab.badge,
                active: tab.active,
                onSelect: () => void tab.onSelect()
            }))}
        />
    )
}

function Users(props: { scope: CompanyUsersScope }): JSX.Element {
    return (
        <Panel
            title="Administradores e profissionais"
            actions={
                <>
                    {/* So o master da empresa, ou a plataforma, cadastra administrador (B-30). */}
                    <Show when={props.scope.podeCadastrarAdministrador}>
                        <Button variant="outline" onClick={() => void props.scope.onNewAdmin()}>
                            <Plus />
                            Administrador
                        </Button>
                    </Show>
                    <Button onClick={() => void props.scope.onNewProfessional()}>
                        <Plus />
                        Profissional
                    </Button>
                </>
            }
            flush
        >
            {/*
                QUANTOS DA EQUIPE ENTRARAM - o indicador que veio de Adocao e Engajamento.
                
                La ele contava CONTAS numa tela que fala de colaboradores, e mudava de
                populacao conforme o filtro de empresa: sem empresa escolhida contava todo
                mundo, inclusive quem administra a plataforma. Tres quartos do numero eram
                login de APP, de colaborador - nada a ver com acesso ao painel.
                
                Aqui a populacao e a da tela, e o numero e contado das LINHAS abaixo: ele nao
                tem como discordar do que se ve.
            */}
            {/*
                Para o administrador que NAO e master, a regra dita uma vez, em vez de
                descoberta botao a botao: quem e o master, e que e com ele (B-30).
            */}
            <Show when={props.scope.avisoMaster}>
                <p class={Css.masterNotice}>{props.scope.avisoMaster}</p>
            </Show>

            {/*
                As tentativas recusadas enquanto a empresa estava bloqueada - so para o
                master e a plataforma, e so depois da liberacao (decisao de 08/10/2026).
            */}
            <Show when={props.scope.blockedAttempts.length > 0}>
                <details class={Css.blockedAttempts}>
                    <summary>
                        <strong>{props.scope.blockedAttempts.length}</strong>{' '}
                        {props.scope.blockedAttempts.length === 1
                            ? 'tentativa de entrar no painel'
                            : 'tentativas de entrar no painel'}{' '}
                        enquanto o acesso da empresa estava bloqueado.
                    </summary>
                    <ul>
                        <For each={props.scope.blockedAttempts}>
                            {(attempt) => (
                                <li>
                                    <span class={Css.attemptWhen}>{attempt.when}</span>
                                    <span>
                                        {attempt.name} · {attempt.email}
                                    </span>
                                </li>
                            )}
                        </For>
                    </ul>
                </details>
            </Show>

            <Show when={props.scope.acessosTotal > 0}>
                <p class={Css.accessSummary}>
                    <strong>{props.scope.acessosRecentes}</strong> de {props.scope.acessosTotal}{' '}
                    {props.scope.acessosTotal === 1 ? 'pessoa entrou' : 'pessoas entraram'} no painel nos últimos 30
                    dias.
                </p>
            </Show>

            <DataTable
                columns={column(props.scope.userColumns)}
                rows={props.scope.users}
                loading={props.scope.loading}
                cell={(row, field) => userCell(row, field as UserField)}
                pinLast
                layout={props.scope.userLayout}
                emptyText="Nenhum usuário nesta empresa"
                /*
                    QUEM ADMINISTRA fica marcado na linha inteira.

                    Sao as pessoas que decidem quem entra, quem sai e o que cada um
                    enxerga - e numa lista em que toda linha parece igual, descobrir
                    quem tem esse poder exigia ler a coluna de papel uma por uma. A
                    faixa atravessa a linha INTEIRA, inclusive a celula presa da
                    direita: e por isso que o fundo vai pela variavel `--row-bg`, e
                    nao por um `bg-` que aquela celula sobrescreveria com branco.
                */
                rowClass={(row) => (row.administrador ? Css.adminRow : '')}
            />
        </Panel>
    )
}

/** Os campos de cada tabela - tipos FECHADOS, para a escolha da celula ser exaustiva. */
type UserField = (typeof USER_COLUMNS)[number]['field']
type LinkField = (typeof LINK_COLUMNS)[number]['field']

/** A celula de uma coluna de usuarios, pelo CAMPO dela - a ordem e a que a pessoa arrastou. */
function userCell(row: CompanyUserScope, field: UserField): JSX.Element {
    switch (field) {
        case 'nome':
            return (
                <button
                    type="button"
                    onClick={() => void row.onOpen()}
                    title="Abrir a ficha desta pessoa"
                    class={Css.drillDown}
                >
                    {row.name}
                </button>
            )
        case 'email':
            return <span>{row.email}</span>
        case 'cpf':
            return (
                <span class={Css.cpfCell}>
                    <span class={Css.number}>{row.cpf}</span>
                    {/*
                    O CPF fabricado a partir do CNPJ, marcado. Sem isto ele passa por
                    identidade de verdade - e o primeiro administrador de TODA empresa
                    tem um.
                */}
                    <Show when={row.cpfFicticio}>
                        <button
                            type="button"
                            onClick={() => void row.onOpen()}
                            title="Este CPF foi gerado a partir do CNPJ. Clique para corrigir."
                            class={Css.generated}
                        >
                            Gerado
                        </button>
                    </Show>
                </span>
            )
        case 'papel':
            return (
                <span class={Css.roleCell}>
                    <span class={Css.roles}>
                        <Show when={row.administrador}>
                            <span class={Css.roleAdmin}>{row.master ? 'Administrador master' : 'Administrador'}</span>
                        </Show>
                        <Show when={row.profissional}>
                            <span class={Css.roleProfessional}>Profissional</span>
                        </Show>
                        <Show when={!row.administrador && !row.profissional}>
                            <span class={Css.missing}>-</span>
                        </Show>
                    </span>

                    {/*
                    A profissao e a DESTA empresa: a mesma pessoa pode ser nutricionista
                    num cliente e preparadora fisica em outro.
                */}
                    <Show when={row.profissao}>{(profissao) => <span class={Css.profession}>{profissao()}</span>}</Show>
                </span>
            )
        case 'colaboradores':
            return (
                // Colaboradores atribuidos so quer dizer algo para quem ACOMPANHA alguem:
                // num administrador puro o numero seria sempre zero, e um zero na coluna
                // parece um profissional sem ninguem.
                <Show when={row.profissional} fallback={<span class={Css.missing}>-</span>}>
                    <span class={Css.number}>{row.atribuidos}</span>
                </Show>
            )
        case 'acesso':
            return (
                /*
                ULTIMO ACESSO - o unico vestigio de login que a plataforma tem.
            
                Nao ha historico de acessos: o que existe e o `iat` do token da ultima sessao.
                Numa lista em que toda linha parece igual, isto e o que separa a conta viva da
                conta que alguem criou e ninguem usa. "Nunca" e resposta, e por isso nao e um
                traco: conta que nunca entrou e um caso, nao um dado faltando.
            */
                <Show when={row.ultimoAcesso} fallback={<span class={Css.missing}>Nunca</span>}>
                    <span>{row.ultimoAcesso}</span>
                </Show>
            )
        case 'acao':
            return (
                <div class={Css.rowActions}>
                    {/*
                    UMA porta para configurar, e nao tres icones.
                
                    Antes havia "atribuir" e "corrigir CPF" lado a lado, e cada um levava a
                    um pedaco: quem abrisse o segundo via um campo de CPF e concluia, com
                    razao, que era so isso que dava para mexer. A ficha mostra tudo.
                */}
                    <RowAction label="Abrir a ficha" onClick={() => void row.onOpen()}>
                        <path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
                    </RowAction>

                    {/* So o que o servidor aceitaria: ver `podeAlterarAcesso` (B-30). */}
                    <Show when={row.podeAlterarAcesso}>
                        <RowAction label="Desligar da empresa" onClick={() => void row.onRemove()}>
                            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 3a4 4 0 1 1 0 8 4 4 0 0 1 0-8M17 11h6" />
                        </RowAction>
                    </Show>
                </div>
            )
        default: {
            // Todo campo tem a sua celula: um campo novo sem `case` nao compila - e, se
            // escapar (um campo vindo de fora do tipo), a celula fica vazia, e nao com o
            // nome do campo escrito.
            const semCelula: never = field
            void semCelula
            return <span />
        }
    }
}

function Requests(props: { scope: CompanyUsersScope }): JSX.Element {
    return (
        <Panel title="Solicitações de vínculo" flush>
            <DataTable
                columns={column(props.scope.linkColumns)}
                rows={props.scope.links}
                loading={props.scope.loading}
                cell={(row, field) => linkCell(row, field as LinkField)}
                pinLast
                layout={props.scope.linkLayout}
                emptyText="Nenhuma solicitação aguardando decisão"
            />
        </Panel>
    )
}

/** A celula de uma coluna de solicitacoes, pelo CAMPO dela. */
function linkCell(row: CompanyLinkScope, field: LinkField): JSX.Element {
    switch (field) {
        case 'nome':
            return <span class={Css.name}>{row.name}</span>
        case 'cpf':
            return <span class={Css.number}>{row.cpf}</span>
        case 'email':
            return <span>{row.email}</span>
        case 'solicitado':
            return <span class={Css.requestedAt}>{row.requestedAt}</span>
        case 'acao':
            return (
                <div class={Css.decision}>
                    <Button class={Css.decisionButton} onClick={() => void row.onApprove()}>
                        Aprovar
                    </Button>
                    <Button variant="outline" class={Css.decisionButton} onClick={() => void row.onReject()}>
                        Recusar
                    </Button>
                </div>
            )
        default: {
            // Todo campo tem a sua celula: um campo novo sem `case` nao compila - e, se
            // escapar (um campo vindo de fora do tipo), a celula fica vazia, e nao com o
            // nome do campo escrito.
            const semCelula: never = field
            void semCelula
            return <span />
        }
    }
}

/**
 * As colunas das duas tabelas: rotulo e lugar, que a pessoa pode arrastar.
 *
 * As tabelas daqui nao ordenam, entao os campos de ordenacao sao fixos aqui, em vez de
 * se repetirem em cada coluna.
 */
function column(columns: readonly CompanyUsersColumnScope[]) {
    return columns.map((item) => ({
        field: item.field,
        label: item.label,
        sortable: false,
        sorted: false,
        descending: false,
        onSort: async () => {},
        movable: item.movable,
        onDrop: (source: string) => item.onDrop(source)
    }))
}

/** O `+` dos dois botoes de cadastro. */
function Plus(): JSX.Element {
    return (
        <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            class={Css.plusIcon}
        >
            <path d="M12 5v14M5 12h14" />
        </svg>
    )
}

/** Um botao de acao na linha - o mesmo desenho da lista de empresas. */
function RowAction(props: { label: string; onClick: () => void; children: JSX.Element }): JSX.Element {
    return (
        <button
            type="button"
            title={props.label}
            aria-label={props.label}
            onClick={props.onClick}
            class={Css.rowAction}
        >
            <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
            >
                {props.children}
            </svg>
        </button>
    )
}
